import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui';

function getInitials(name) {
  if (!name) return '';
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
}

const SectionTitle = ({ icon, children }) => (
  <div className="flex items-center gap-2 text-emerald-700 font-semibold text-base mt-6 mb-2">
    {icon && <span className="text-xl">{icon}</span>}
    <span>{children}</span>
  </div>
);

const Divider = () => <div className="border-b border-gray-100 my-3" />;

const CustomerInfoCard = ({
  customer,
  appointments = [],
  recommendedPractitioner,
  recommendedTreatment,
  onFillPractitioner,
  onFillTreatment,
  searchTerm,
  onCustomerSearch,
  filteredCustomers = [],
  showCustomerDropdown,
  onCustomerSelect,
  formData
}) => {
  // Avatar: use image if available, else initials
  const avatar = customer?.avatar
    ? <img src={customer.avatar} alt="avatar" className="w-16 h-16 rounded-full object-cover border" />
    : <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-2xl font-bold text-emerald-700 border">{getInitials(customer?.name)}</div>;

  return (
    <div className="bg-white border border-emerald-100 rounded-2xl shadow-sm p-6 min-h-[400px] text-base">
      {/* Customer Search/Select */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">客户 <span className="text-red-500">*</span></label>
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={onCustomerSearch}
            onFocus={() => {}}
            placeholder="搜索客户姓名或电话"
            className="w-full px-4 py-2 text-base border border-gray-300 bg-white text-gray-900 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200"
            required
            autoComplete="off"
          />
          <input type="hidden" name="customerId" value={formData.customerId} required />
          {showCustomerDropdown && filteredCustomers.length > 0 && (
            <div className="absolute z-20 mt-2 w-full bg-white shadow-xl max-h-60 rounded-xl py-2 border border-gray-200 overflow-auto">
              {filteredCustomers.map((c) => (
                <div
                  key={c.id}
                  onClick={() => onCustomerSelect(c)}
                  className="cursor-pointer px-4 py-2 hover:bg-emerald-50 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{c.name}</span>
                    <span className="text-sm text-gray-500">{c.phoneNumber || '无电话'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* If no customer selected, show tip */}
      {!customer && (
        <div className="text-gray-400 text-base text-center py-8">请选择客户以查看详细信息和推荐</div>
      )}

      {customer && (
        <>
          {/* Section 1: Basic Info */}
          <div className="flex items-center gap-4 mb-2">
            {avatar}
            <div>
              <div className="text-xl font-bold text-gray-900 flex items-center gap-2">
                {customer.name}
                {customer.gender && <span className="text-base text-gray-500">{customer.gender}</span>}
                {customer.age && <span className="text-base text-gray-500">/ {customer.age}岁</span>}
              </div>
              <div className="text-base text-gray-500 mt-1">{customer.phoneNumber || '无电话'}</div>
              <div className="text-base text-gray-500 mt-1">类型: {customer.customerType === 'returning' ? '老客户' : customer.customerType === 'new' ? '新客户' : '未知'}</div>
              {customer.tags && customer.tags.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {customer.tags.map(tag => (
                    <span key={tag} className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-base">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <Divider />

          {/* Section 2: Health/Customer Info */}
          <SectionTitle icon={<span>🩺</span>}>客户信息</SectionTitle>
          {customer.currentSymptoms && customer.currentSymptoms.length > 0 && (
            <div className="text-base text-gray-700 mb-1">主诉: {customer.currentSymptoms.join(', ')}</div>
          )}
          {customer.specialNeeds && (
            <div className="text-base text-orange-600 mb-1">特殊需求: {customer.specialNeeds}</div>
          )}
          {customer.allergies && customer.allergies.length > 0 && (
            <div className="text-base text-red-500 mb-1">过敏史: {customer.allergies.map(a => a.allergen).join(', ')}</div>
          )}
          {customer.medicalHistory && customer.medicalHistory.length > 0 && (
            <div className="text-base text-gray-500 mb-1">既往病史: {customer.medicalHistory.map(m => m.condition).join(', ')}</div>
          )}
          {customer.notes && (
            <div className="text-base text-gray-500 mb-1">备注: {customer.notes}</div>
          )}
          <Divider />

          {/* Section 3: Follow-up/Visit Info */}
          <SectionTitle icon={<span>📅</span>}>随访与到访信息</SectionTitle>
          <div className="text-base text-gray-700 mb-1">到访次数: {customer.visitCount || 0}</div>
          <div className="text-base text-gray-700 mb-1">最近到访: {customer.lastVisitDate ? new Date(customer.lastVisitDate).toLocaleDateString() : '无'}</div>
          {appointments.length > 0 && (
            <div className="text-base text-gray-500 mb-1">历史预约: {appointments.length} 次</div>
          )}
          {appointments.length > 0 && appointments[0]?.treatmentName && (
            <div className="text-base text-gray-500 mb-1">最近项目: {appointments[0].treatmentName}</div>
          )}
          <div className="flex flex-col gap-3 mt-3">
            {recommendedPractitioner && (
              <Button type="button" size="medium" variant="primary" className="flex items-center gap-2 !text-base !font-semibold !px-4 !py-2 !rounded-lg shadow hover:shadow-md transition" onClick={onFillPractitioner}>
                <span role="img" aria-label="practitioner">👤</span> 推荐调理师: {recommendedPractitioner.name}
                <span className="text-xs text-emerald-700 ml-2">点击可自动填入</span>
              </Button>
            )}
            {recommendedTreatment && (
              <Button type="button" size="medium" variant="primary" className="flex items-center gap-2 !text-base !font-semibold !px-4 !py-2 !rounded-lg shadow hover:shadow-md transition" onClick={onFillTreatment}>
                <span role="img" aria-label="project">💡</span> 推荐项目: {recommendedTreatment.name}
                <span className="text-xs text-emerald-700 ml-2">点击可自动填入</span>
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

CustomerInfoCard.propTypes = {
  customer: PropTypes.object,
  appointments: PropTypes.array,
  recommendedPractitioner: PropTypes.object,
  recommendedTreatment: PropTypes.object,
  onFillPractitioner: PropTypes.func,
  onFillTreatment: PropTypes.func,
  searchTerm: PropTypes.string,
  onCustomerSearch: PropTypes.func,
  filteredCustomers: PropTypes.array,
  showCustomerDropdown: PropTypes.bool,
  onCustomerSelect: PropTypes.func,
  formData: PropTypes.object,
};

export default CustomerInfoCard; 